import { useQuery } from "@apollo/client";
import {
  ColorSchemeProvider,
  LoadingOverlay,
  MantineProvider,
} from "@mantine/core";
import { useHotkeys, useLocalStorage } from "@mantine/hooks";
import {
  NotificationsProvider,
  showNotification,
} from "@mantine/notifications";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { AUTH } from "apollo/queries";
import { authDataVar } from "apollo/store";
import Layout from "components/layout/Layout";
import { customLoader } from "components/utilities/loader";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import RoutesComp from "./routes";

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [position, setPosition] = useState(null);

  // Routes that don't require a sidebar/header layout
  const noLayoutRoutes = [
    "/login",
    "/retailer_terms",
    "/supplier_terms",
    "/driver_terms",
    "/register",
  ];

  // Routes that shouldn't trigger a hard redirect to login if auth fails
  const publicRoutes = [
    "/retailer_terms",
    "/supplier_terms",
    "/driver_terms",
    "/register",
  ];

  const shouldUseLayout = !noLayoutRoutes.includes(location.pathname);
  const isPublicRoute = publicRoutes.includes(location.pathname);

  const { loading, refetch } = useQuery(AUTH, {
    fetchPolicy: "network-only", // Ensures it doesn't just pull from cache
    onCompleted(data) {
      authDataVar(data);
    },
    onError(error) {
      const errorMsg = error?.graphQLErrors?.[0]?.message;
      console.error("Auth query error:", errorMsg);

      // Handle unauthenticated states
      if (
        errorMsg === "Unauthenticated." ||
        errorMsg === "Token has expired. Please log in again."
      ) {
        localStorage.removeItem("auth_token");
        localStorage.removeItem("permissions");
        localStorage.removeItem("roles");

        if (!isPublicRoute && location.pathname !== "/login") {
          navigate("/login", { replace: true });
          showNotification({
            color: "yellow",
            title: "Session Expired",
            message: "Your session has expired. Please log in again.",
          });
        }
      }
    },
  });

  // Effect 1: Handle Document Title and Initial Token Setup
  useEffect(() => {
    const host = window.location.hostname;
    const port = window.location.port;

    if (host === "admin.ant.et") {
      document.title = "ANT B2B";
    } else if (host === "78.47.138.239" && port === "8081") {
      document.title = "ANT B2B DEV";
    } else {
      document.title = "ANT B2B";
    }

    const token = localStorage.getItem("auth_token");
    if (!token && !isPublicRoute) {
      navigate("/login", { replace: true });
    }
  }, []);

  // Effect 2: Trigger re-fetch on every navigation
  useEffect(() => {
    refetch();
  }, [location.pathname, refetch]);

  // Theme & Appearance Logic
  const theme = createTheme({});
  const [colorScheme, setColorScheme] = useLocalStorage({
    key: "mantine-color-scheme",
    defaultValue: "light",
    getInitialValueInEffect: true,
  });

  const toggleColorScheme = (value) =>
    setColorScheme(value || (colorScheme === "dark" ? "light" : "dark"));

  useHotkeys([["mod+J", () => toggleColorScheme()]]);

  return (
    <ThemeProvider theme={theme}>
      <ColorSchemeProvider
        colorScheme={colorScheme}
        toggleColorScheme={toggleColorScheme}
      >
        <MantineProvider
          withGlobalStyles
          withNormalizeCSS
          theme={{
            colorScheme,
            breakpoints: {
              xs: 500,
              sm: 800,
              md: 1000,
              lg: 1200,
              xl: 1400,
            },
          }}
        >
          {/* NotificationsProvider wrapped once to avoid duplication */}
          <NotificationsProvider position={position}>
            {loading ? (
              <LoadingOverlay
                visible={loading}
                color="blue"
                overlayBlur={2}
                loader={customLoader}
              />
            ) : shouldUseLayout ? (
              <Layout setPosition={setPosition}>
                <RoutesComp />
              </Layout>
            ) : (
              <RoutesComp />
            )}
          </NotificationsProvider>
        </MantineProvider>
      </ColorSchemeProvider>
    </ThemeProvider>
  );
}

export default App;